import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchCustomerUsageWithRefresh, CustomerUsageData } from "../dataaccess/fetchCustomerUsagePublicAPI";
import { fetchModelsUsage, ModelUsageData } from "../dataaccess/fetchModelsPublicAPI";
import { CustomerCallsGrid } from "./CustomerCallsGrid";
import { tokenManager } from "../services/tokenManager";

interface CustomerUsageWidgetProps {
  customerId: string;
  projectId: string;
  jwtToken?: string;
  apiKey?: string; // Deprecated: Use jwtToken instead
  theme?: "light" | "dark" | "auto";
  compact?: boolean;
  showTierInfo?: boolean;
  showReplenishmentInfo?: boolean;
  showCallsGrid?: boolean;
  callsGridTitle?: string;
  callsGridMaxHeight?: number;
  className?: string;
}

interface UsageItemData {
  category: string;
  tierType: string;
  remaining: number;
  allocated: number;
  unlimited: boolean;
  percentage: number;
  used: number;
  hasAllocation: boolean;
  displayText: string;
  isOverage: boolean;
  overageAmount: number;
}

export const CustomerUsageWidget: React.FC<CustomerUsageWidgetProps> = ({
  customerId,
  projectId,
  jwtToken,
  apiKey, // Deprecated: backward compatibility
  theme = "light",
  compact = false,
  showTierInfo = true,
  showReplenishmentInfo = true,
  showCallsGrid = false,
  callsGridTitle = "Calls since last refresh",
  callsGridMaxHeight = 400,
  className,
}) => {
  // For now, allow rendering without authentication to prevent breaking existing usage
  // The actual data fetching will handle the authentication requirement
  const hasAuth = Boolean(jwtToken || apiKey);

  // Initialize token manager with JWT token if provided
  useEffect(() => {
    if (jwtToken) {
      tokenManager.setToken({
        token: jwtToken,
        expiresAt: new Date(Date.now() + 3600000).toISOString(), // Default 1 hour expiry
        expiresIn: 3600
      });
    }
  }, [jwtToken]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["customer-usage-widget", projectId, customerId, jwtToken ? "jwt" : "apikey"],
    queryFn: () => {
      if (!jwtToken && !apiKey) {
        throw new Error('Either jwtToken or apiKey must be provided for data fetching');
      }
      return fetchCustomerUsageWithRefresh(projectId, customerId, apiKey, jwtToken);
    },
    staleTime: 60 * 1000, // 1 minute - shorter than admin portal for embeds
    retry: 2,
    enabled: hasAuth, // Only run query when we have authentication
  });

  // Fetch models usage data for better model tier detection
  const { data: modelsData, isLoading: modelsLoading } = useQuery({
    queryKey: ["models-usage-widget"],
    queryFn: () => fetchModelsUsage(),
    staleTime: 15 * 60 * 1000, // 15 minutes - models change less frequently
    retry: 2,
    enabled: showCallsGrid, // Only fetch when calls grid is shown
  });

  useEffect(() => {
    // Log the theme for debugging purposes
    console.log("Models data fetched:", modelsData);
  }, [modelsData]);

  const customer = data?.data?.[0] as CustomerUsageData | undefined;
  const models = modelsData?.data || [];



  // Helper function to format numbers for display
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  // Helper function to check if a tier/type has unlimited usage
  const isUnlimited = (tierType: string, usageType: "token" | "call"): boolean => {
    if (!customer) return false;
    const key = `${tierType}${usageType === "token" ? "Token" : "Call"}Unlimited`;
    const customerValue = customer[key as keyof CustomerUsageData] as boolean;
    const tierValue = customer.usageTier?.[key as keyof typeof customer.usageTier] as boolean;
    return Boolean(customerValue || tierValue);
  };

  // Helper function to get replenishment amounts
  const getReplenishmentAmount = (tierType: string, usageType: "token" | "call") => {
    if (!customer?.usageTier) return 0;
    const key = `${tierType}${usageType === "token" ? "Token" : "Call"}ReplenishmentAmount`;
    return customer.usageTier[key as keyof typeof customer.usageTier] as number || 0;
  };

  // Helper function to get display value for usage
  const getDisplayValue = (remaining: number, allocated: number, tierType: string, usageType: "token" | "call") => {
    if (isUnlimited(tierType, usageType)) {
      return "Unlimited";
    }
    if (allocated === 0) {
      // Show used amount when no tier allocation
      const used = Math.abs(remaining);
      return `${formatNumber(used)} used`;
    }
    const used = allocated - remaining;
    return `${formatNumber(used)} / ${formatNumber(allocated)}`;
  };

  // Helper function to get usage details for display
  const getUsageDetails = (remaining: number, allocated: number, tierType: string, usageType: "token" | "call") => {
    if (isUnlimited(tierType, usageType)) {
      const used = Math.abs(remaining);
      return {
        used,
        allocated: "∞",
        displayText: `${formatNumber(used)} / ∞`,
        hasAllocation: true,
        isOverage: false,
        overageAmount: 0
      };
    }
    
    if (allocated === 0) {
      // No tier allocation - check if customer has any usage
      const used = Math.abs(remaining);
      if (used === 0) {
        // No allocation and no usage - tier not allowed
        return {
          used: 0,
          allocated: 0,
          displayText: "None allowed",
          hasAllocation: false,
          isOverage: false,
          overageAmount: 0
        };
      } else {
        // No allocation but has usage (possibly from before tier change)
        return {
          used,
          allocated: 0,
          displayText: `${formatNumber(used)} used`,
          hasAllocation: false,
          isOverage: false,
          overageAmount: 0
        };
      }
    }
    
    const used = allocated - remaining;
    const isOverage = used > allocated;
    const overageAmount = isOverage ? used - allocated : 0;
    
    return {
      used,
      allocated,
      displayText: `${formatNumber(used)} / ${formatNumber(allocated)}`,
      hasAllocation: true,
      isOverage,
      overageAmount
    };
  };

  // Helper function to get usage percentage
  const getUsagePercentage = (remaining: number, allocated: number) => {
    if (allocated === 0) return 0;
    const used = allocated - remaining;
    return Math.max(0, Math.min(100, (used / allocated) * 100));
  };

  // Prepare usage data for display
  const prepareUsageData = (usageType: "token" | "call"): UsageItemData[] => {
    if (!customer) return [];

    const tiers = ["standard", "premium", "reasoning", "superPremium"];
    return tiers.map((tierType) => {
      const remainingKey = `${tierType}${usageType === "token" ? "Token" : "Call"}sRemaining`;
      const remaining = customer[remainingKey as keyof CustomerUsageData] as number || 0;
      const allocated = getReplenishmentAmount(tierType, usageType);
      const unlimited = isUnlimited(tierType, usageType);
      const usageDetails = getUsageDetails(remaining, allocated, tierType, usageType);
      
      return {
        category: tierType === "superPremium" ? "Super\nPremium" : tierType.charAt(0).toUpperCase() + tierType.slice(1),
        tierType,
        remaining,
        allocated,
        unlimited,
        percentage: getUsagePercentage(remaining, allocated),
        used: usageDetails.used,
        hasAllocation: usageDetails.hasAllocation,
        displayText: usageDetails.displayText,
        isOverage: usageDetails.isOverage,
        overageAmount: usageDetails.overageAmount,
      };
    });
  };

  const callUsageData = prepareUsageData("call");
  const tokenUsageData = prepareUsageData("token");

  // Calculate total usage across all tiers
  const totalCallsUsed = callUsageData.reduce((sum, item) => sum + item.used, 0);
  const totalTokensUsed = tokenUsageData.reduce((sum, item) => sum + item.used, 0);

  // Calculate total allocated across all tiers (for customers with tier allocations)
  const totalCallsAllocated = callUsageData.reduce((sum, item) => 
    item.unlimited ? Infinity : sum + (item.allocated || 0), 0);
  const totalTokensAllocated = tokenUsageData.reduce((sum, item) => 
    item.unlimited ? Infinity : sum + (item.allocated || 0), 0);

  // Check if customer has overage billing enabled
  const hasOverageBilling = customer?.usageLimitAction === "ALLOW" || customer?.usageTier?.usageLimitAction === "ALLOW";
  
  // Calculate total overage amounts
  const totalCallOverage = callUsageData.reduce((sum, item) => sum + item.overageAmount, 0);
  const totalTokenOverage = tokenUsageData.reduce((sum, item) => sum + item.overageAmount, 0);
  const hasAnyOverage = totalCallOverage > 0 || totalTokenOverage > 0;

  // Colors for different tiers
  const colors = {
    standard: "#1976d2",
    premium: "#2e7d32", 
    superPremium: "#ed6c02",
    reasoning: "#d32f2f",
  };

  // Format next replenishment date
  const getNextReplenishmentDate = () => {
    if (!customer?.nextReplenishAt) return "Not scheduled";
    return new Date(customer.nextReplenishAt).toLocaleDateString();
  };

  if (isLoading || (showCallsGrid && modelsLoading)) {
    return (
      <Paper className={className} sx={{ p: compact ? 2 : 3, textAlign: "center" }}>
        <CircularProgress size={compact ? 24 : 32} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading usage data...
        </Typography>
      </Paper>
    );
  }

  if (!hasAuth) {
    return (
      <Paper className={className} sx={{ p: compact ? 2 : 3 }}>
        <Alert severity="warning">
          <Typography variant={compact ? "body2" : "body1"}>
            Authentication required
          </Typography>
          {!compact && (
            <Typography variant="caption" color="text.secondary">
              Please provide either a JWT token or API key to load usage data
            </Typography>
          )}
        </Alert>
      </Paper>
    );
  }

  if (isError) {
    return (
      <Paper className={className} sx={{ p: compact ? 2 : 3 }}>
        <Alert severity="error">
          <Typography variant={compact ? "body2" : "body1"}>
            Failed to load usage data
          </Typography>
          {!compact && (
            <Typography variant="caption" color="text.secondary">
              {error instanceof Error ? error.message : "Unknown error occurred"}
            </Typography>
          )}
        </Alert>
      </Paper>
    );
  }

  if (!customer) {
    return (
      <Paper className={className} sx={{ p: compact ? 2 : 3 }}>
        <Alert severity="warning">
          <Typography variant={compact ? "body2" : "body1"}>
            Customer not found
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper className={className} sx={{ p: compact ? 2 : 3, maxWidth: compact ? 400 : 800 }}>
      {/* Header */}
      <Typography variant={compact ? "subtitle1" : "h6"} gutterBottom>
        Usage Dashboard
        {!compact && customer.name && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            - {customer.name}
          </Typography>
        )}
      </Typography>

      {/* Usage Tier Information */}
      {showTierInfo && customer.usageTier && (
        <Box sx={{ mb: compact ? 2 : 3 }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <Typography variant="body2" color="text.secondary">
                Plan:
              </Typography>
            </Grid>
            <Grid item>
              <Chip
                label={`${customer.usageTier.name}${customer.usageTier.version ? ` v${customer.usageTier.version}` : ""}`}
                color="primary"
                variant="outlined"
                size={compact ? "small" : "medium"}
              />
            </Grid>
            {!compact && (
              <>
                <Grid item>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    Limit Action:
                  </Typography>
                </Grid>
                <Grid item>
                  <Chip
                    label={
                      customer.usageTier.usageLimitAction === "ALLOW" ? "Overage Billing" :
                      customer.usageTier.usageLimitAction === "BLOCK" ? "Block at Limit" :
                      customer.usageLimitAction === "ALLOW" ? "Overage Billing" :
                      customer.usageLimitAction === "BLOCK" ? "Block at Limit" :
                      "Not set"
                    }
                    color={
                      (customer.usageTier.usageLimitAction === "ALLOW" || customer.usageLimitAction === "ALLOW") ? "warning" :
                      (customer.usageTier.usageLimitAction === "BLOCK" || customer.usageLimitAction === "BLOCK") ? "error" : "default"
                    }
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      )}

      {/* Overage Billing Notice */}
      {hasOverageBilling && hasAnyOverage && !compact && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Overage Usage Detected
            </Typography>
            <Typography variant="body2">
              You have exceeded your plan limits. Additional usage will be billed at overage rates.
            </Typography>
            {totalCallOverage > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                • <strong>{formatNumber(totalCallOverage)} excess calls</strong> (above plan limits)
              </Typography>
            )}
            {totalTokenOverage > 0 && (
              <Typography variant="body2">
                • <strong>{formatNumber(totalTokenOverage)} excess tokens</strong> (above plan limits)
              </Typography>
            )}
          </Alert>
        </Box>
      )}

      {/* Overage Billing Info */}
      {hasOverageBilling && !hasAnyOverage && !compact && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Overage Billing Enabled:</strong> You can exceed your plan limits. Additional usage will be billed separately.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Calls Usage */}
      <Box sx={{ mb: compact ? 2 : 3 }}>
        <Typography variant={compact ? "subtitle2" : "subtitle1"} gutterBottom>
          Call Usage
        </Typography>
        <Grid container spacing={compact ? 1 : 2}>
          {callUsageData.map((item, index) => (
            <Grid item xs={compact ? 6 : 3} key={`call-${index}`}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {item.category}
                </Typography>
                <Typography 
                  variant={compact ? "body2" : "body1"} 
                  sx={{ 
                    fontWeight: "medium",
                    color: item.isOverage && hasOverageBilling ? "warning.main" : "inherit"
                  }}
                >
                  {item.displayText}
                  {item.isOverage && hasOverageBilling && (
                    <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                      (+{formatNumber(item.overageAmount)} overage)
                    </Typography>
                  )}
                </Typography>
                {/* Additional details line for current usage */}
                {!compact && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.unlimited ? `${formatNumber(item.used)} calls used` :
                     item.hasAllocation ? `${formatNumber(item.used)} used, ${formatNumber(item.remaining)} remaining` :
                     item.displayText === "None allowed" ? "Not included in your plan" :
                     `Total calls: ${formatNumber(item.used)}`}
                    {item.isOverage && hasOverageBilling && (
                      <Typography component="span" color="warning.main" sx={{ ml: 1 }}>
                        (billing applies to overage)
                      </Typography>
                    )}
                  </Typography>
                )}
                {!item.unlimited && item.allocated > 0 && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, item.percentage)} // Cap at 100% for visual clarity
                    sx={{
                      mt: 0.5,
                      height: compact ? 4 : 6,
                      backgroundColor: "rgba(0,0,0,0.1)",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: item.isOverage && hasOverageBilling ? 
                          "#ff9800" : // Orange for overage
                          colors[item.tierType as keyof typeof colors],
                      },
                    }}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {!compact && <Divider sx={{ my: 2 }} />}

      {/* Tokens Usage */}
      <Box sx={{ mb: compact ? 2 : 3 }}>
        <Typography variant={compact ? "subtitle2" : "subtitle1"} gutterBottom>
          Token Usage
        </Typography>
        <Grid container spacing={compact ? 1 : 2}>
          {tokenUsageData.map((item, index) => (
            <Grid item xs={compact ? 6 : 3} key={`token-${index}`}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {item.category}
                </Typography>
                <Typography 
                  variant={compact ? "body2" : "body1"} 
                  sx={{ 
                    fontWeight: "medium",
                    color: item.isOverage && hasOverageBilling ? "warning.main" : "inherit"
                  }}
                >
                  {item.displayText}
                  {item.isOverage && hasOverageBilling && (
                    <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                      (+{formatNumber(item.overageAmount)} overage)
                    </Typography>
                  )}
                </Typography>
                {/* Additional details line for current usage */}
                {!compact && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.unlimited ? `${formatNumber(item.used)} tokens used` :
                     item.hasAllocation ? `${formatNumber(item.used)} used, ${formatNumber(item.remaining)} remaining` :
                     item.displayText === "None allowed" ? "Not included in your plan" :
                     `Total tokens: ${formatNumber(item.used)}`}
                    {item.isOverage && hasOverageBilling && (
                      <Typography component="span" color="warning.main" sx={{ ml: 1 }}>
                        (billing applies to overage)
                      </Typography>
                    )}
                  </Typography>
                )}
                {!item.unlimited && item.allocated > 0 && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, item.percentage)} // Cap at 100% for visual clarity
                    sx={{
                      mt: 0.5,
                      height: compact ? 4 : 6,
                      backgroundColor: "rgba(0,0,0,0.1)",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: item.isOverage && hasOverageBilling ? 
                          "#ff9800" : // Orange for overage
                          colors[item.tierType as keyof typeof colors],
                      },
                    }}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Replenishment Information */}
      {showReplenishmentInfo && customer.usageTier && !compact && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Replenishment
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Period:
                </Typography>
                <Typography variant="body2">
                  {customer.usageTier.replenishmentPeriod || "Not set"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Next Reset:
                </Typography>
                <Typography variant="body2">
                  {getNextReplenishmentDate()}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </>
      )}

      {/* API Calls Grid */}
      {showCallsGrid && !compact && (
        <>
          <Divider sx={{ my: 3 }} />
          <Box>
            <CustomerCallsGrid
              customerId={customerId}
              projectId={projectId}
              jwtToken={jwtToken}
              apiKey={apiKey}
              lastReplenishedAt={customer?.lastReplenishedAt}
              title={callsGridTitle}
              maxHeight={callsGridMaxHeight}
              compact={false}
              modelsData={models}
            />
          </Box>
        </>
      )}
    </Paper>
  );
};
